import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  BarChart3, TrendingUp, TrendingDown, Trophy, Target, Filter, RefreshCw, Loader2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const ESTADO_COLOR = {
  nuevo: "bg-zinc-500",
  contactado: "bg-blue-500",
  reunion_agendada: "bg-indigo-500",
  presupuesto_enviado: "bg-amber-500",
  negociacion: "bg-orange-500",
  ganado: "bg-emerald-500",
  perdido: "bg-red-500",
  descartado: "bg-red-700",
  descartado_sin_respuesta: "bg-rose-600",
  convertido: "bg-emerald-700",
};

const ESTADO_LABEL = {
  nuevo: "Nuevo",
  contactado: "Contactado",
  reunion_agendada: "Reunión agendada",
  presupuesto_enviado: "Presupuesto enviado",
  negociacion: "Negociación",
  ganado: "Ganado",
  perdido: "Perdido",
  descartado: "Descartado",
  descartado_sin_respuesta: "Sin respuesta (auto)",
  convertido: "Convertido",
};

export default function LeadsAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const ago90 = new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [desde, setDesde] = useState(ago90);
  const [hasta, setHasta] = useState(today);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      const { data } = await api.get(`/leads-analytics?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const correrCheckInactivos = async () => {
    if (!confirm("¿Ejecutar auto-descarte de leads inactivos ahora?\nMoverá a 'descartado_sin_respuesta' los leads sin actualización en > dias_validez_oferta.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/leads-analytics/_check-inactivos");
      toast.success(`✅ Procesados: ${data.procesados} · Descartados: ${data.descartados}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error ejecutando check");
    } finally {
      setRunning(false);
    }
  };

  const totalEmbudo = useMemo(() => {
    return (data?.embudo || []).reduce((a, b) => a + (b.n || 0), 0) || 1;
  }, [data]);

  const maxFuente = useMemo(() => {
    return Math.max(...((data?.fuentes || []).map((f) => f.n || 0)), 1);
  }, [data]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh] text-zinc-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando analytics…
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="leads-analytics-page">
      <PageHeader
        title="Leads · Analítica"
        subtitle="Embudo, fuentes y conversión · feedback de marketing (Elia)"
        icon={BarChart3}
      />

      {/* Filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-zinc-400">Desde</Label>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
            className="bg-zinc-900 border-zinc-800 w-40" data-testid="filtro-desde" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Hasta</Label>
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
            className="bg-zinc-900 border-zinc-800 w-40" data-testid="filtro-hasta" />
        </div>
        <Button onClick={load} className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-semibold" data-testid="btn-aplicar">
          <Filter className="w-4 h-4 mr-2" /> Aplicar
        </Button>
        <div className="ml-auto">
          <Button onClick={correrCheckInactivos} disabled={running} variant="outline"
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid="btn-check-inactivos">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Auto-descartar inactivos
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Total leads" value={data.total} icon={Target} color="text-zinc-100" />
        <KPI label="Ganados" value={data.ganados} icon={Trophy} color="text-emerald-300" testid="kpi-ganados" />
        <KPI label="Perdidos" value={data.perdidos} icon={TrendingDown} color="text-rose-300" />
        <KPI label="Tasa conversión" value={`${data.tasa_conversion_pct}%`} icon={TrendingUp} color="text-amber-300" testid="kpi-conversion" />
        <KPI label="Importe ganado" value={eur(data.importe_ganado)} icon={Sparkles} color="text-emerald-300" testid="kpi-importe-ganado" />
      </div>

      {/* Embudo */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-zinc-100">Embudo por estado</h3>
          <span className="text-xs text-zinc-500">Pipeline ponderado: <b className="text-amber-300">{eur(data.importe_pipeline_ponderado)}</b></span>
        </div>
        <div className="space-y-2" data-testid="embudo-list">
          {data.embudo.length === 0 && <div className="text-sm text-zinc-500 py-4 text-center">Sin datos para el periodo seleccionado.</div>}
          {data.embudo.map((row) => {
            const pct = (row.n / totalEmbudo) * 100;
            return (
              <div key={row.estado} className="flex items-center gap-3" data-testid={`embudo-row-${row.estado}`}>
                <div className="w-44 text-xs text-zinc-300 truncate">{ESTADO_LABEL[row.estado] || row.estado}</div>
                <div className="flex-1 h-7 bg-zinc-950 rounded-md overflow-hidden border border-zinc-800/60">
                  <div className={`h-full ${ESTADO_COLOR[row.estado] || "bg-zinc-500"} flex items-center justify-end pr-2 transition-all`}
                       style={{ width: `${Math.max(pct, 4)}%` }}>
                    <span className="text-[11px] font-bold text-zinc-900">{row.n}</span>
                  </div>
                </div>
                <div className="w-12 text-right text-[11px] text-zinc-500 font-mono">{pct.toFixed(0)}%</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fuentes + Motivos pérdida */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-zinc-100 mb-3">Origen del lead · Top fuentes</h3>
          <div className="space-y-2" data-testid="fuentes-list">
            {data.fuentes.length === 0 && <div className="text-sm text-zinc-500 py-4 text-center">Sin fuentes registradas.</div>}
            {data.fuentes.slice(0, 10).map((f) => {
              const pct = (f.n / maxFuente) * 100;
              return (
                <div key={f.fuente} className="flex items-center gap-3" data-testid={`fuente-row-${f.fuente}`}>
                  <div className="w-32 text-xs text-zinc-300 truncate">{f.fuente}</div>
                  <div className="flex-1 h-5 bg-zinc-950 rounded border border-zinc-800/60 overflow-hidden">
                    <div className="h-full bg-amber-500/80" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-10 text-right text-xs text-zinc-200 font-mono">{f.n}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-zinc-100 mb-3">Motivos de pérdida</h3>
          <div className="space-y-2" data-testid="motivos-list">
            {data.motivos_perdida.length === 0 && <div className="text-sm text-zinc-500 py-4 text-center">Sin motivos registrados todavía.</div>}
            {data.motivos_perdida.slice(0, 10).map((m) => (
              <div key={m.motivo} className="flex items-center justify-between text-xs px-3 py-2 bg-zinc-950/60 rounded border border-zinc-800/60">
                <span className="text-zinc-300 truncate pr-2">{m.motivo}</span>
                <span className="text-rose-300 font-mono">{m.n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className={`text-2xl font-bold ${color || "text-zinc-100"}`}>{value}</div>
    </div>
  );
}
